import { thresholdScott } from "d3";
import { clarityValues, observatoryMetadata } from "../config";
import { d3, DateTime } from "../imports";
import { createLoadingSpinner, startUpperCase } from "../utils";

import { getCurrentObservatoryName, changeUTCTimeToLocalTime } from "../utils";

const margin = { top: 20, right: 50, bottom: 40, left: 80 };
const width = 600;
const height = 350;

// move into config? (along with above values)
const minBarWidthPixels = 20;
export const maxDataPoints = Math.floor(
  (width - margin.right - margin.left) / minBarWidthPixels
);

const formatMillisecond = d3.utcFormat(".%L"),
  formatSecond = d3.utcFormat(":%S"),
  formatMinute = d3.utcFormat("%H:%M"),
  formatHour = d3.utcFormat("%H:%M"),
  formatDay = d3.utcFormat("%b %d"),
  formatWeek = d3.utcFormat("%b %d"),
  formatMonth = d3.utcFormat("%b"),
  formatYear = d3.utcFormat("%Y");

function multiFormat(date) {
  return (
    d3.utcSecond(date) < date
      ? formatMillisecond
      : d3.utcMinute(date) < date
      ? formatSecond
      : d3.utcHour(date) < date
      ? formatMinute
      : d3.utcDay(date) < date
      ? formatHour
      : d3.utcMonth(date) < date
      ? d3.utcWeek(date) < date
        ? formatDay
        : formatWeek
      : d3.utcYear(date) < date
      ? formatMonth
      : formatYear
  )(date);
}

/**
 * Create a div to stand in for a graph when there isn't one
 * @param {string} graphDivSelector
 * @returns {Node} The hidden div
 */
function createGraphPlaceholders(graphDivSelector) {
  const graphDiv = document.querySelector(graphDivSelector);

  const noDataDiv = document.createElement("div");
  noDataDiv.classList.add("no-graph-placeholder");
  noDataDiv.classList.add("hide");
  const innerNoDataDiv = document.createElement("div");
  innerNoDataDiv.innerText = "No data";
  noDataDiv.appendChild(innerNoDataDiv);
  graphDiv.insertAdjacentElement("beforebegin", noDataDiv);

  const loadingDiv = document.createElement("div");
  const spinnerDiv = createLoadingSpinner();
  loadingDiv.classList.add("loading-graph-placeholder");
  loadingDiv.appendChild(spinnerDiv);
  graphDiv.insertAdjacentElement("beforebegin", loadingDiv);

  return [noDataDiv, loadingDiv];
}

const bisect = d3.bisector(function (d) {
  return d.x;
}).left;

function filterNanToZero(value) {
  if (!isNaN(value) && value !== null) {
    return value;
  } else {
    return 0;
  }
}

function makeFlatArray(nestedArray, key) {
  const flatArray = [];

  nestedArray.forEach((d) => {
    flatArray.push(d[key][0]);
    flatArray.push(d[key][1]);
  });

  return flatArray;
}

/**
 * Create the base SVG container at 100% Width and 100% Height
 *
 * @param {string} parentID
 * @returns {Object[SVGAElement]} The SVG
 */
function createBaseSVG(parentID, square = false) {
  let thisWidth = width;
  if (square) {
    thisWidth = height;
  }
  return d3
    .select(parentID)
    .append("svg")
    .attr("width", "100%")
    .attr("height", "100%")
    .attr("preserveAspectRatio", "xMidYMid meet")
    .attr("viewBox", `0 0 ${thisWidth} ${height}`)
    .classed("svg-content", true)
    .classed("dashboard-graph", true);
}

/**
 * Create the xScaler (with time)
 *
 * @param {number} minX
 * @param {number} maxX
 * @returns {d3.ScaleTime} The xScaler
 */
function createXScalerTime(minX, maxX) {
  return d3
    .scaleUtc()
    .domain([d3.isoParse(minX + "Z"), d3.isoParse(maxX + "Z")])
    .range([margin["left"], width - margin["right"]]);
}

/**
 * Create the yScaler (linear)
 *
 * @param {number} minY
 * @param {number} maxY
 * @returns {d3.ScaleLinear}
 */
function createYScalerLinear(minY, maxY) {
  return d3
    .scaleLinear()
    .domain([minY, maxY])
    .nice()
    .rangeRound([height - margin["bottom"], margin["top"]]);
}

function setUpXAxis(baseSVG, xScaler) {
  const xAxis = baseSVG
    .append("g")
    .attr("class", "xaxis")
    .attr("transform", `translate(0,${height - margin["bottom"]})`)
    .call(
      d3
        .axisBottom(xScaler)
        .ticks(width / 60)
        .tickFormat(multiFormat)
        .tickSizeOuter(0)
    );

  return xAxis;
}

function setUpYAxis(baseSVG, yScaler) {
  const yAxis = baseSVG
    .append("g")
    .attr("class", "yaxis")
    .attr("transform", `translate(${margin["left"]},0)`)
    .call(
      d3
        .axisLeft(yScaler)
        .ticks(height / 40)
        .tickSizeInner(-width + margin["left"] + margin["right"])
    );

  return yAxis;
}

function setUpY2Axis(baseSVG, yScaler) {
  const yAxis = baseSVG
    .append("g")
    .attr("class", "y2axis")
    .attr("transform", `translate(${width - margin["right"]},0)`)
    .call(d3.axisRight(yScaler).ticks(height / 40));

  return yAxis;
}

function setUpXLabel(baseSVG, xLabel) {
  baseSVG
    .append("g")
    .append("text")
    .attr("class", "xaxistext")
    .attr("x", width / 2)
    .attr("y", height - 4)
    .attr("text-anchor", "center")
    .attr("vertical-align", "bottom")
    .text(xLabel);
}

function setUpYLabel(baseSVG, yLabel) {
  baseSVG
    .append("g")
    .append("text")
    .attr("class", "yaxistext")
    .attr("x", margin.left)
    .attr("y", 8)
    .attr("text-anchor", "start")
    .text(yLabel);
}

function setUpY2Label(baseSVG, yLabel) {
  baseSVG
    .append("g")
    .append("text")
    .attr("class", "y2axistext")
    .attr("x", width - margin.right)
    .attr("y", 8)
    .attr("text-anchor", "end")
    .text(yLabel);
}

export class DashboardTimeLineGraph {
  constructor(
    parentID,
    xLabel,
    yLabel,
    y2Label = null,
    xLim = ["2025-02-06T23:00:00", "2025-02-07T00:00:00"],
    yLim = [0, 1000],
    y2Lim = [0, 2000],
    createPlaceholders = true
  ) {
    this.divID = parentID;
    this.xLabel = xLabel;
    this.yLabel = yLabel;
    this.y2Label = y2Label;

    this.xLim = xLim;
    this.yLim = yLim;
    this.y2Lim = y2Lim;

    this.plotElements = new Object();

    if (createPlaceholders) {
      [this.noDataDiv, this.loadingDiv] = createGraphPlaceholders(parentID);
    } else {
      this.noDataDiv = null;
      this.loadingDiv = null;
    }

    this.svg = createBaseSVG(parentID);

    //   Setting up X and Y Scalers
    this.xScaler = createXScalerTime(xLim[0], xLim[1]);
    this.yScaler = createYScalerLinear(yLim[0], yLim[1]);

    // Setting up X-Axis
    this.xAxis = setUpXAxis(this.svg, this.xScaler);

    // Setting up X-Axis Label
    setUpXLabel(this.svg, this.xLabel);

    // Setting up Y-Axis
    this.yAxis = setUpYAxis(this.svg, this.yScaler);

    // Setting up Y-Axis Labels
    setUpYLabel(this.svg, this.yLabel);

    // Setting up dual Y-axis
    if (y2Label != null) {
      this.yScaler2 = createYScalerLinear(y2Lim[0], y2Lim[1]);
      this.yAxis2 = setUpY2Axis(this.svg, this.yScaler2);
      setUpY2Label(this.svg, this.y2Label);
    }
  }

  makeGraphKey(graphType, xKey, yKey) {
    let graphKey = `${graphType}:${xKey}:${yKey}`;

    if (this.plotElements.hasOwnProperty(graphKey)) {
      graphKey = graphKey + window.crypto.randomUUID();
    }

    return graphKey;
  }

  /**
   * Show just the loading spinner
   */
  showLoading() {
    if (this.noDataDiv) {
      this.noDataDiv.classList.add("hide");
    }
    if (this.loadingDiv) {
      this.loadingDiv.classList.remove("hide");
    }
  }

  getYAxis(data, yKey, secondAxis, staticYlims) {
    let currentYaxis = this.yAxis;
    let currentYScaler = this.yScaler;
    if (secondAxis) {
      currentYaxis = this.yAxis2;
      currentYScaler = this.yScaler2;
    }

    if (staticYlims === null) {
      currentYScaler.domain(d3.extent(data, (d) => d[yKey]));
    } else {
      currentYScaler.domain(staticYlims);
    }

    return [currentYaxis, currentYScaler];
  }

  /**
   * Show appropriate items if the data is null, otherwise hide all placeholders
   * Show or hide the SVG as determined by the hideSVG.
   *
   * Hiding the SVG is used on the first creation, since graphs are created and then
   * immediately reloaded
   * @param {Object[]} data
   * @param {boolean} hideSVG
   * @returns {boolean}
   */
  controlPlaceholders(data, yKey, hideSVG) {
    if (data.every((d) => d[yKey] === null)) {
      // show no data
      if (this.loadingDiv) {
        this.loadingDiv.classList.add("hide");
      }
      if (this.noDataDiv) {
        this.noDataDiv.classList.remove("hide");
      }
      return false;
    }
    if (this.loadingDiv) {
      // hide spinner
      this.loadingDiv.classList.add("hide");
    }
    if (this.noDataDiv) {
      // hide no data
      this.noDataDiv.classList.add("hide");
    }
    if (hideSVG) {
      this.svg.classed("hide", true);
    } else {
      this.svg.classed("hide", false);
    }
    return true;
  }

  /**
   * Create a Bar Graph on the DashboardTimeLineGraph
   *
   * @param {Object} data
   * @param {string} xKey
   * @param {string} yKey
   * @param {Object} options
   * @param {boolean} [options.secondAxis=false]
   * @param {boolean} [options.transition=true]
   * @param {Number[]|null} [options.staticYlims=null]
   * @param {string[]} [options.extraClasses=[]]
   * @param {boolean} [options.hideSVG=false]
   * @returns {string} key for graph
   */
  makeBarGraph(data, xKey, yKey, options) {
    const {
      secondAxis = false,
      transition = true,
      staticYlims = null,
      extraClasses = [],
      hideSVG = false,
      staticXlims = null,
    } = options;

    const graphKey = this.makeGraphKey("bar", xKey, yKey);

    const continueGraph = this.controlPlaceholders(data, yKey, hideSVG);
    if (!continueGraph) {
      return graphKey;
    }

    // set up axes
    const [currentYaxis, currentYScaler] = this.getYAxis(
      data,
      yKey,
      secondAxis,
      staticYlims
    );
    const flatDates = makeFlatArray(data, xKey);

    if (staticXlims) {
      this.xLim = staticXlims;
      this.xScaler.domain(d3.extent(staticXlims, (d) => d3.isoParse(d + "Z")));
    } else {
      this.xScaler.domain(d3.extent(flatDates, (d) => d3.isoParse(d + "Z")));
    }

    // helper functions for setting data
    const getBarXLeft = (d) => this.xScaler(d3.isoParse(d[xKey][0] + "Z"));
    const getBarXRight = (d) => this.xScaler(d3.isoParse(d[xKey][1] + "Z"));
    const getBarY = (d) => currentYScaler(filterNanToZero(d[yKey]));
    const getBarWidth = (d) => Math.floor(getBarXRight(d) - getBarXLeft(d));

    const barClasses = "main-data main-data-bar " + extraClasses.join(" ");

    // Initially Plotting everything at Zero
    this.plotElements[graphKey] = this.svg
      .selectAll()
      .data(data)
      .join("rect")
      .attr("x", (d) => getBarXLeft(d))
      .attr("y", (d) => currentYScaler(0))
      .attr("width", (d) => getBarWidth(d))
      .attr("height", 0)
      .attr("class", barClasses);

    // Updating to new data
    this.plotElements[graphKey]
      .enter()
      .append("rect")
      .merge(this.plotElements[graphKey])
      .transition()
      .duration(1000)
      .attr("x", (d) => getBarXLeft(d))
      .attr("y", (d) => getBarY(d))
      .attr("width", (d) => getBarWidth(d))
      .attr("height", (d) => height - getBarY(d) - margin.bottom)
      .attr("class", barClasses);

    if (transition) {
      this.transitionAxis();
    }

    return graphKey;
  }

  /**
   * Create a Line Graph on the DashboardTimeLineGraph
   *
   * @param {Object} data
   * @param {string} xKey
   * @param {string} yKey
   * @param {Object} options
   * @param {boolean} [options.secondAxis=false]
   * @param {boolean} [options.transition=true]
   * @param {Number[]|null} [options.staticYlims=null]
   * @param {string[]} [options.extraClasses=[]]
   * @param {boolean} [options.hideSVG=false]
   * @returns {string} key for graph
   */
  makeLineGraph(data, xKey, yKey, options) {
    const {
      secondAxis = false,
      transition = true,
      staticYlims = null,
      extraClasses = [],
      hideSVG = false,
      staticXlims = null,
    } = options;

    const graphKey = this.makeGraphKey("line", xKey, yKey);

    // set up axes
    const [currentYaxis, currentYScaler] = this.getYAxis(
      data,
      yKey,
      secondAxis,
      staticYlims
    );
    if (staticXlims) {
      this.xLim = staticXlims;
      this.xScaler.domain(d3.extent(staticXlims, (d) => d3.isoParse(d + "Z")));
    } else {
      this.xScaler.domain(d3.extent(data, (d) => d3.isoParse(d[xKey] + "Z")));
    }

    const continueGraph = this.controlPlaceholders(data, yKey, hideSVG);
    if (!continueGraph) {
      if (transition) {
        // Use the new axes even if there's no data (only works if both x and ylims are given)
        this.transitionAxis();
      }
      return graphKey;
    }

    const line1 = d3
      .line()
      .defined((d) => yKey in d && !(d[yKey] === null))
      .x((d) => this.xScaler(d3.isoParse(d[xKey] + "Z")))
      .y((d) => currentYScaler(d[yKey]))
      .curve(d3.curveBasis);

    // plot the line hidden at first
    this.plotElements[graphKey] = this.svg
      .append("path")
      .datum(data)
      .attr("class", "main-data main-data-line hide " + extraClasses.join(" "))
      .attr("d", line1);

    // show the line
    this.plotElements[graphKey]
      .classed("hide", false)
      .classed("main-data-line-animate", true);

    if (transition) {
      this.transitionAxis();
    }

    return graphKey;
  }

  /**
   * Transition Axis to Current Scalers
   */
  transitionAxis() {
    this.yAxis
      .transition()
      .duration(250)
      .call(
        d3
          .axisLeft(this.yScaler)
          .tickSizeInner(-width + margin["left"] + margin["right"])
      );

    this.xAxis
      .transition()
      .duration(250)
      .call(
        d3
          .axisBottom(this.xScaler)
          .ticks(width / 60)
          .tickFormat(multiFormat)
          .tickSizeOuter(0)
      );

    if (this.yAxis2) {
      this.yAxis2
        .transition()
        .duration(250)
        .call(d3.axisRight(this.yScaler2).tickSizeInner(20));
    }
  }

  updateData(graphKey, data, xKey, yKey, options) {
    const {
      secondAxis = false,
      transition = true,
      staticYlims = null,
      extraClasses = [],
      hideSVG = false,
      staticXlims = null,
    } = options;

    this.controlPlaceholders(data, yKey, hideSVG);

    // handle cases where graphkey wasn't defined for whatever reason
    if (!this.plotElements.hasOwnProperty(graphKey)) {
      if (graphKey.startsWith("bar")) {
        this.makeBarGraph(data, xKey, yKey, options);
      }
      if (graphKey.startsWith("line")) {
        this.makeLineGraph(data, xKey, yKey, options);
      }
      return;
    }

    // set up axes
    const [currentYaxis, currentYScaler] = this.getYAxis(
      data,
      yKey,
      secondAxis,
      staticYlims
    );

    // Getting new X-domain
    if (staticXlims) {
      this.xLim = staticXlims;
      this.xScaler.domain(d3.extent(staticXlims, (d) => d3.isoParse(d + "Z")));
    } else if (graphKey.startsWith("bar")) {
      const flatDates = makeFlatArray(data, xKey);
      this.xScaler.domain(d3.extent(flatDates, (d) => d3.isoParse(d + "Z")));
    } else {
      this.xScaler.domain(d3.extent(data, (d) => d3.isoParse(d[xKey] + "Z")));
    }

    if (graphKey.startsWith("bar")) {
      // helper functions for setting data
      const getBarXLeft = (d) => this.xScaler(d3.isoParse(d[xKey][0] + "Z"));
      const getBarXRight = (d) => this.xScaler(d3.isoParse(d[xKey][1] + "Z"));
      const getBarY = (d) => currentYScaler(filterNanToZero(d[yKey]));
      const getBarWidth = (d) => Math.floor(getBarXRight(d) - getBarXLeft(d));

      // clear out old data, by keeping the x and zero-ing the y
      const oldData = this.plotElements[graphKey].data();
      oldData.forEach((row) => (row[yKey] = 0));
      this.plotElements[graphKey]
        .data(oldData)
        .enter()
        .append("rect")
        .merge(this.plotElements[graphKey])
        .attr("x", (d) => getBarXLeft(d))
        .attr("y", (d) => getBarY(d))
        .attr("width", (d) => getBarWidth(d))
        .attr("height", (d) => height - getBarY(d) - margin.bottom)
        .attr("class", "main-data main-data-bar " + extraClasses.join(" "));

      // Apply the new data
      this.plotElements[graphKey]
        .data(data)
        .enter()
        .append("rect")
        .merge(this.plotElements[graphKey])
        .transition()
        .duration(1000)
        .attr("x", (d) => getBarXLeft(d))
        .attr("y", (d) => getBarY(d))
        .attr("width", (d) => getBarWidth(d))
        .attr("height", (d) => height - getBarY(d) - margin.bottom)
        .attr("class", "main-data main-data-bar " + extraClasses.join(" "));
    }

    if (graphKey.startsWith("line")) {
      const line1 = d3
        .line()
        .defined((d) => yKey in d && !(d[yKey] === null))
        .x((d) => this.xScaler(d3.isoParse(d[xKey] + "Z")))
        .y((d) => currentYScaler(d[yKey]))
        .curve(d3.curveBasis);

      this.plotElements[graphKey]
        .datum(data)
        .enter()
        .append("path")
        .merge(this.plotElements[graphKey])
        .transition()
        .delay(500)
        .duration(1000)
        .attr("class", "main-data main-data-line " + extraClasses.join(" "))
        .attr("d", line1);
    }

    this.plotElements[graphKey].exit().remove();
    if (transition) {
      this.transitionAxis();
    }
  }

  addNowLine() {
    const observatoryName = getCurrentObservatoryName();
    const obsTZ = observatoryMetadata[observatoryName].TZInfo;
    const now = DateTime.now().toUTC();
    this.nowLine = null;
    this.nowLineData = [
      { x: changeUTCTimeToLocalTime(now.toISO(), obsTZ), y: -90.0 },
      { x: changeUTCTimeToLocalTime(now.toISO(), obsTZ), y: 90.0 },
    ];

    this.nowLineFunc = d3
      .line()
      .x((d) => {
        return this.xScaler(d3.isoParse(d["x"] + "Z"));
      })
      .y((d) => {
        return this.yScaler(d["y"]);
      });

    this.nowLineGraphic = this.svg
      .append("path")
      .datum(this.nowLineData)
      .attr("class", "target-now-line")
      .attr("d", this.nowLineFunc);

    this.nowLineLabel = this.svg
      .append("text")
      .attr("id", "nowLineLabel")
      .attr("x", this.xScaler(d3.isoParse(this.nowLineData[0].x + "Z")))
      .attr("y", this.yScaler.range()[1] - 5)
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "text-bottom")
      .text("Current Time");
  }

  refreshNowLine() {
    const observatoryName = getCurrentObservatoryName();
    const obsTZ = observatoryMetadata[observatoryName].TZInfo;
    const now = DateTime.now().toUTC();
    this.nowLine = null;
    this.nowLineData = [
      { x: changeUTCTimeToLocalTime(now.toISO(), obsTZ), y: -90.0 },
      { x: changeUTCTimeToLocalTime(now.toISO(), obsTZ), y: 90.0 },
    ];

    this.nowLineGraphic
      .datum(this.nowLineData)
      .enter()
      .append("path")
      .merge(this.nowLineGraphic)
      .transition()
      .duration(500)
      .attr("class", "target-now-line")
      .attr("d", this.nowLineFunc);

    this.nowLineLabel
      .transition()
      .delay(100)
      .duration(1000)
      .attr("x", this.xScaler(d3.isoParse(this.nowLineData[0].x + "Z")));
  }

  addClarityLines(observatoryName) {
    const thresholds = clarityValues[observatoryName].cloud;

    for (let clarityLabel in thresholds) {
      const textOffset = width / 24;
      const clarityData = [
        {
          x: this.xScaler(d3.isoParse(this.xLim[0] + "Z")),
          y: this.yScaler(thresholds[clarityLabel]),
        },
        {
          x: this.xScaler(d3.isoParse(this.xLim[1] + "Z")) + textOffset,
          y: this.yScaler(thresholds[clarityLabel]),
        },
      ];

      const lineFunction = d3
        .line()
        .x((d) => d.x)
        .y((d) => d.y);

      this.svg
        .append("path")
        .datum(clarityData)
        .attr("class", `clarity-line clarity-${clarityLabel}`)
        .attr("d", lineFunction);

      this.svg
        .append("text")
        .attr("class", "clarity-text")
        .attr("x", clarityData[1].x)
        .attr("y", clarityData[1].y)
        .text(startUpperCase(clarityLabel));
    }
  }
}

export class WindRoseGraph {
  constructor(parentID, data) {
    // Create the div and SVG
    this.divID = parentID;
    this.svg = createBaseSVG(parentID, true);

    // Set variables
    const innerRadius = height / 35;
    const outerRadius = height / 2 - 25;
    const angleOffset = -11.25; // how much to offset the degree bins

    // The relevant keys to use for the plotting
    this.columns = [
      "angle_bin_name",
      "0.0 to 0.5 m/s",
      "0.5 to 1.0 m/s",
      "1.0 to 1.5 m/s",
      "1.5 to 2.0 m/s",
      "2.0 to 2.5 m/s",
      "2.5 to 3.0 m/s",
      "3.0 to 3.5 m/s",
      "3.5+ m/s",
    ];
    const colourmap = [
      "#f7f2ff",
      "#dfd2f7",
      "#cab6f2",
      "#a88ae6",
      "#9167e6",
      "#8250e6",
      "#6231c4",
      "#4211a6",
    ];

    // Create stacked arrays of the data
    const stackedData = d3.stack().keys(this.columns.slice(1))(data);

    // the xScale is for the bars, one full turn around circle
    const x = d3
      .scaleBand()
      .range([0, 2 * Math.PI])
      .domain(data.map((d) => d.angle_bin_name))
      .align(0);

    // yScale is for the height of the bars
    const y = d3
      .scaleLinear()
      .range([innerRadius, outerRadius])
      .domain([0, d3.max(data, (d) => d.total)]);

    // Move the graph to the center
    const g = this.svg
      .append("g")
      .attr("transform", `translate(${height / 2},${height / 2})`);

    // set the colour scale for the wind speed
    this.colorScale = d3
      .scaleOrdinal()
      .domain(this.columns.slice(1))
      .range(colourmap.reverse());

    const makeArc = d3
      .arc()
      .innerRadius((d) => y(d[0]))
      .outerRadius((d) => y(d[1]))
      .startAngle((d) => x(d.data.angle_bin_name))
      .endAngle((d) => x(d.data.angle_bin_name) + x.bandwidth())
      .padAngle(0.02)
      .padRadius(innerRadius);

    function radAxis(g) {
      g.append("g")
        .attr("class", "rad-axis")
        .selectAll("g")
        .data(y.ticks(5)) // .slice(1) to skip 0
        .join("g")
        .call((g) =>
          g.append("circle").attr("stroke-dasharray", "4,4").attr("r", y)
        );
    }

    // add windrose background circle
    g.append("circle")
      .attr("r", outerRadius + 25)
      .attr("class", "windrose-background");

    // Set up the circular axis lines
    g.append("g")
      .attr("class", "axes")
      .selectAll(".axis")
      .data(d3.range(0, 360, 360 / data.length))
      .join("g")
      .attr("class", "circ-axis")
      .attr("transform", (d) => `rotate(${d - 90})`)
      .append("line")
      .attr("x1", innerRadius)
      .attr("x2", y(y.ticks(5).reverse()[0])) // to the last tick circle
      .attr("fill", "none")
      .attr("stroke-dasharray", "1,4");

    // Plot the histogram
    g.append("g")
      .attr("class", "rings")
      .selectAll(".ring")
      .data(stackedData)
      .join((enter) =>
        enter
          .append("g")
          .attr("fill", (d) => this.colorScale(d.key))
          .selectAll("path")
          .data((d) => d)
          .join("path")
          .attr("d", makeArc)
          .attr("transform", `rotate(${angleOffset})`)
      );

    // Add the direction labels
    const label = g
      .append("g")
      .attr("class", "direction-labels")
      .selectAll("g")
      .data(data)
      .join("g")
      .attr("text-anchor", "middle")
      .attr(
        "transform",
        (d) =>
          `rotate(${
            ((x(d.angle_bin_name) + x.bandwidth() / 2) * 180) / Math.PI -
            (90 - angleOffset)
          }) translate(${outerRadius + 12},0)`
      );

    // Selects the labels in the lower half of the circle
    const inLowerHalf = (d) =>
      (x(d.angle_bin_name) + x.bandwidth() / 2 + Math.PI / 2) % (2 * Math.PI) <
      Math.PI;

    // Flips the labels on the bottom
    label
      .append("text")
      .attr("transform", (d) =>
        inLowerHalf(d)
          ? "rotate(90)translate(0,6)"
          : "rotate(-90)translate(0,6)"
      ) // flip bottom labels
      .text((d) => d.angle_bin_name);

    // Set up the radial axis
    g.call(radAxis);
  }

  // Creates the legend SVG
  addLegend(parentID) {
    const rectSize = 20;
    const rectPadding = 10;
    const legendWidth = 320;
    const legendHeight =
      ((this.columns.length - 1) / 2) * (rectSize + rectPadding);

    const svg = d3
      .select(parentID)
      .append("svg")
      .attr("width", legendWidth)
      .attr("height", legendHeight);

    // add background box
    svg
      .append("rect")
      .attr("class", "legend")
      .attr("width", legendWidth)
      .attr("height", legendHeight);

    function position(d, i, colLength = this.columns.length) {
      const c = 2;
      const r = Math.ceil(colLength.length / c);
      const h = rectSize + 5; // height of each entry
      const w = legendWidth / 2.1; // width of each entry (so you can position the next column)
      const tx = rectPadding; // tx/ty are essentially margin values
      const ty = rectPadding;
      const x = Math.floor(i / r) * w + tx;
      const y = (i % r) * h + ty;
      return "translate(" + x + "," + y + ")";
    }

    // add legend entries
    svg
      .append("g")
      .attr("class", "legend")
      .selectAll("g")
      .data(this.columns.slice(1).reverse())
      .join("g")
      .attr("transform", position)
      .call((g) =>
        g
          .append("rect")
          .attr("width", rectSize)
          .attr("height", rectSize)
          .attr("fill", this.colorScale)
      )
      .call((g) =>
        g
          .append("text")
          .attr("x", "2rem")
          .attr("y", "0.6rem")
          .attr("dy", "0.3rem")
          .attr("class", "legend-text")
          .text((d) => `${d}`)
      )
      .append("text");

    return svg.node();
  }
}

export class HistogramGraph {
  constructor(parentID, yKey, xLabel, yLabel, xLims, yLim = [0, 30]) {
    this.divID = parentID;
    this.xLabel = xLabel;
    this.yLabel = yLabel;

    this.xLim = xLims;
    this.yLim = yLim;

    this.plotElements = new Object();

    [this.noDataDiv, this.loadingDiv] = createGraphPlaceholders(parentID);
    this.svg = createBaseSVG(parentID);

    //   Setting up X and Y Scalers
    this.xScaler = d3
      .scaleBand()
      .domain(xLims)
      .range([margin["left"], width - margin["right"]]);
    this.yScaler = createYScalerLinear(yLim[0], yLim[1]);

    // Setting up X-Axis
    this.xAxis = setUpXAxis(this.svg, this.xScaler);

    // Setting up X-Axis Label
    setUpXLabel(this.svg, this.xLabel);

    // Setting up Y-Axis
    this.yAxis = setUpYAxis(this.svg, this.yScaler);

    // Setting up Y-Axis Labels
    setUpYLabel(this.svg, this.yLabel);

    // Initially Plotting everything at Zero
    const barClasses = "main-data main-data-bar";
    const graphKey = `hist:${yKey}`;
    this.plotElements[graphKey] = this.svg
      .selectAll()
      .data(this.xLim)
      .join("rect")
      .attr("x", (d) => this.xScaler(d))
      .attr("y", (d) => this.yScaler(0))
      .attr("width", this.xScaler.bandwidth())
      .attr("height", 0)
      .attr("class", barClasses);
  }

  makeGraphKey(graphType, yKey) {
    let graphKey = `${graphType}:${yKey}`;

    if (this.plotElements.hasOwnProperty(graphKey)) {
      graphKey = graphKey + window.crypto.randomUUID();
    }

    return graphKey;
  }

  /**
   * Show just the loading spinner
   */
  showLoading() {
    this.svg.classed("hide", true);
    this.noDataDiv.classList.add("hide");
    this.loadingDiv.classList.remove("hide");
  }

  /**
   * Show appropriate items if the data is null, otherwise hide all placeholders
   * Show or hide the SVG as determined by the hideSVG.
   *
   * Hiding the SVG is used on the first creation, since graphs are created and then
   * immediately reloaded
   * @param {Object[]} data
   * @param {boolean} hideSVG
   * @returns {boolean}
   */
  controlPlaceholders(data, hideSVG) {
    if (data === null || data.length === 0) {
      // show no data
      this.svg.classed("hide", true);
      this.loadingDiv.classList.add("hide");
      this.noDataDiv.classList.remove("hide");
      return false;
    }
    // hide spinner
    this.loadingDiv.classList.add("hide");
    // hide no data
    this.noDataDiv.classList.add("hide");

    if (hideSVG) {
      this.svg.classed("hide", true);
    } else {
      this.svg.classed("hide", false);
    }
    return true;
  }

  plotHistogram(data, yKey, options) {
    const { transition = true, extraClasses = [], hideSVG = false } = options;

    // plot the data
    const graphKey = `hist:${yKey}`;
    const continueGraph = this.controlPlaceholders(data, hideSVG);
    if (!continueGraph) {
      return graphKey;
    }

    // bin the data
    const bins = d3
      .bin()
      .domain([this.xLim[0], this.xLim[this.xLim.length - 1]])
      .thresholds(this.xLim)
      .value((d) => d[yKey])(data);

    // set up axes
    this.yScaler.domain([0, d3.max(bins, (d) => d.length)]);

    // helper functions for setting data
    const getBarXLeft = (d) => this.xScaler(d.x0);
    const getBarXRight = (d) => this.xScaler(d.x1);
    const getBarY = (d) => this.yScaler(filterNanToZero(d.length));
    //const getBarWidth = (d) => Math.floor(getBarXRight(d) - getBarXLeft(d));

    const barClasses = "main-data main-data-bar " + extraClasses.join(" ");

    // Updating to new data
    this.plotElements[graphKey]
      .data(bins)
      .enter()
      .append("rect")
      .merge(this.plotElements[graphKey])
      .transition()
      .duration(1000)
      .attr("x", (d) => getBarXLeft(d))
      .attr("y", (d) => getBarY(d))
      .attr("width", this.xScaler.bandwidth())
      .attr("height", (d) => height - getBarY(d) - margin.bottom)
      .attr("class", barClasses);

    this.plotElements[graphKey].exit().remove();
    if (transition) {
      this.transitionAxis();
    }
    return graphKey;
  }

  /**
   * Transition Axis to Current Scalers
   */
  transitionAxis() {
    this.yAxis
      .transition()
      .duration(250)
      .call(
        d3
          .axisLeft(this.yScaler)
          .tickSizeInner(-width + margin["left"] + margin["right"])
      );

    this.xAxis
      .transition()
      .duration(250)
      .call(
        d3
          .axisBottom(this.xScaler)
          .ticks(width / 40)
          .tickSizeOuter(0)
      );
  }
}
